//librerías
import { useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleCheck,
  faTriangleExclamation,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

//context
import { AuthContext } from "../../../context/AuthContext";

//estilos
const classes = require("./ValidacionCell.module.css").default;

// Tipo mínimo requerido para mostrar el nombre del validador.
// Estructural para ser compatible con los distintos tipos de Usuario
// usados en las tablas de servicios.
interface ValidadorInfo {
  nombre?: string | null;
  apellido_paterno?: string | null;
}

interface Props {
  idActividad: number;
  validadoPor: number | null | undefined;
  validador: ValidadorInfo | null | undefined;
  loading: boolean;
  onValidar: (idActividad: number) => void;
  onDesvalidar: (idActividad: number) => void;
}

/**
 * ValidacionCell Component
 * @description: Celda de estado de validación de un servicio. Maneja dos
 *   estados mutuamente excluyentes:
 *   - Validado: badge verde "✓ Validado: {usuario}" con botón ✕ integrado
 *     (anular validación) visible solo para quien validó.
 *   - Pendiente: badge ámbar "⚠ Pendiente de validar" con botón ✓ integrado
 *     (validar) visible solo para usuarios con permiso "Actividad:validar".
 */
const ValidacionCell = ({
  idActividad,
  validadoPor,
  validador,
  loading,
  onValidar,
  onDesvalidar,
}: Props) => {
  const authContext = useContext(AuthContext);
  const puedeValidar = authContext.tokenCan("Actividad:validar");

  // ESTADO: Validado
  if (validadoPor) {
    const nombreValidador = validador
      ? `${validador.nombre ?? ""} ${validador.apellido_paterno ?? ""}`.trim()
      : "Usuario";
    // Solo el usuario que validó puede anular la validación
    const esValidador =
      puedeValidar && authContext.usuario?.id === validadoPor;

    return (
      <div className={classes.validationCell}>
        <div className={`${classes.badge} ${classes.badgeSuccess}`}>
          <span className={classes.badgeContent}>
            <FontAwesomeIcon
              className={classes.icono}
              icon={faCircleCheck as IconProp}
            />
            Validado: {nombreValidador}
          </span>
          {esValidador && (
            <button
              type="button"
              className={classes.btnIconSmall}
              title="Anular validación"
              aria-label={`Anular validación de ${nombreValidador}`}
              disabled={loading}
              onClick={() => onDesvalidar(idActividad)}
            >
              <FontAwesomeIcon icon={faXmark as IconProp} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ESTADO: Pendiente (sin permiso → solo texto)
  if (!puedeValidar) {
    return (
      <div className={classes.validationCell}>
        <div className={`${classes.badge} ${classes.badgeWarning}`}>
          <span className={classes.badgeContent}>
            <FontAwesomeIcon
              className={classes.icono}
              icon={faTriangleExclamation as IconProp}
            />
            Pendiente de validar
          </span>
        </div>
      </div>
    );
  }

  // ESTADO: Pendiente (con permiso → botón ✓ para validar)
  return (
    <div className={classes.validationCell}>
      <div className={`${classes.badge} ${classes.badgeWarning}`}>
        <span className={classes.badgeContent}>
          <FontAwesomeIcon
            className={classes.icono}
            icon={faTriangleExclamation as IconProp}
          />
          {loading ? "Validando..." : "Pendiente de validar"}
        </span>
        <button
          type="button"
          className={classes.btnSmall}
          title="Validar"
          aria-label="Validar servicio"
          disabled={loading}
          onClick={() => onValidar(idActividad)}
        >
          <FontAwesomeIcon icon={faCheck as IconProp} />
        </button>
      </div>
    </div>
  );
};

export default ValidacionCell;
